import { $, Html, html } from 'rune-ts';
import { font, staticFont } from '@services/MarppleShop/shared/typography';
import { InputChangedEvent } from '@services/MarppleShop/plusStudioApp/components/atoms/Input/Input.ts';
import {
  PureInputType,
  PureInput,
  pureInputKlass,
  pureInputVariant,
} from '../../../../../../shared/rune-component/Input/PureInput.ts';
import { controlErrorMessage } from '@services/MarppleShopStudio/shared/component/atom/Input/core/helper.ts';
import { Input_Base } from '@services/MarppleShopStudio/shared/component/atom/Input/core/Input_Base.ts';
import { InputEditor } from '@services/MarppleShopStudio/shared/component/atom/Input/core/types.ts';
import { EditorInputFocusoutEvent } from '@services/MarppleShopStudio/shared/component/atom/Input/core/events.ts';
import klass from './core/input.module.scss';

interface InputTextData<TName extends string = string> extends InputEditor {
  value: string;
  name: TName;
  onInput?: (view: PureInputType) => void;
  option?: {
    max_length?: number;
    placeholder?: string;
    min_rows?: number;
    max_rows?: number;
  };
}

export class Input_TextEditor<TName extends string = string> extends Input_Base<InputTextData<TName>> {
  private inputView = new PureInput({
    variant: pureInputVariant.studio_editor,
    name: this.data.name,
    value: this.data.value,
    placeholder: this.data.option?.placeholder,
    maxlength: this.data.option?.max_length ? this.data.option?.max_length : undefined,
    min_rows: this.data.option?.min_rows,
    max_rows: this.data.option?.max_rows,
    onInput: (view) => {
      this.validMaxLength(view);
      this.data?.onInput?.(view);
    },
    onFocusout: (view) => {
      this.dispatchEvent(EditorInputFocusoutEvent, {
        bubbles: true,
        detail: {
          name: view.getName(),
        },
      });
    },
  });

  protected inputTemplate() {
    return html`
      <div class="${klass.input_container}">
        ${this.inputView}
        <span class="${klass.error_message} ${font('12_medium')}"></span>
      </div>
    `;
  }

  override titleRightHtml(): Html {
    return this.data.option?.max_length
      ? html`
          <div class="${klass.max_length} ${staticFont('unica_12_regular')}">
            <span class="${klass.current_length}">${this.data.value.length}</span>/${this.data.option
              ?.max_length}
          </div>
        `
      : html``;
  }

  override onRender() {
    /* maxLength 존재하는 경우 인풋 우측 상단에 maxLength 표시 해주기 위한 핸들러 */
    this.addEventListener(InputChangedEvent, () => {
      const max_length = this.data.option?.max_length;
      if (!max_length) {
        return;
      }

      const current_value = this.inputView.getValue();

      $(this.element())
        .find(`.${klass.max_length}`)
        ?.chain((el) => {
          if (current_value.length > max_length) {
            el.classList.add(klass.error);
          } else {
            el.classList.remove(klass.error);
          }
        })
        ?.find(`.${klass.current_length}`)
        ?.setTextContent(`${current_value.length}`);
    });
  }

  private validMaxLength(view: PureInputType) {
    const max_length = this.data.option?.max_length;
    if (!max_length) return;

    const value = view.getValue();
    const max_length$ = $(this.element()).find(`.${klass.max_length}`);

    if (value.length > max_length) {
      max_length$?.addClass(klass.error);
    } else {
      max_length$?.removeClass(klass.error);
    }

    max_length$?.find(`.${klass.current_length}`)?.setTextContent(value.length.toString());
  }

  public override setError(is_error: boolean, message?: string): void {
    if (is_error) {
      this.inputView.addKlass(pureInputKlass.error);
    } else {
      this.inputView.removeKlass(pureInputKlass.error);
    }

    controlErrorMessage(this.element(), message);
  }

  public getName(): TName {
    return this.data.name;
  }

  public getValue(): string {
    return this.inputView.getValue();
  }

  public setValue(value: string) {
    this.inputView.setValue(value);
    this.data.value = value;
    return this;
  }

  public setDisabled(value: boolean) {
    this.inputView.data.disabled = value;
    this.inputView.setKlass(`${value ? klass.disabled_input : ''}`);
    return this;
  }
}
